"use client"

import { type Cell, flexRender, type HeaderGroup, type Row } from "@tanstack/react-table"
import React from "react"
import { Loader2 } from "lucide-react"
import { Card, CardContent } from "visuais"
import { ScrollArea } from "visuais"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "visuais"

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value)
}

export function InclusaoFaturasDataTable({ table, isLoading, error }: any) {
  const [page, setPage] = React.useState(0)
  const pageSize = 100 // evita render pesado

  // Calcula total parcial (ajuste "VALOR" para o campo correto)
  const totalParcial = table
    .getRowModel()
    .rows.filter((row: any) => !!row.original?.checked)
    .reduce((acc: number, row: any) => acc + (Number(row.original?.VALOR) || 0), 0)

  const allRows: Row<any>[] = table.getRowModel().rows
  const totalRows = allRows.length
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize))
  const currentPage = Math.min(page, totalPages - 1)
  const pagedRows = React.useMemo(
    () => allRows.slice(currentPage * pageSize, currentPage * pageSize + pageSize),
    [allRows, currentPage],
  )

  return (
    <Card className="flex-1 flex flex-col overflow-hidden py-1">
      <CardContent className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-1 min-h-0 overflow-hidden">
          <ScrollArea className="h-full">
            {error && (
              <div className="p-4 text-center text-destructive">{`Erro ao carregar dados: ${error.message}`}</div>
            )}
            {!error && (
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup: HeaderGroup<any>) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                  {/* Linha de total parcial */}
                  <TableRow className="bg-muted/50 font-medium">
                    <TableCell colSpan={table.getAllColumns().length} className="text-right pr-5">
                      Total: {formatCurrency(totalParcial)}
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={table.getAllColumns().length} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Carregando...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={table.getAllColumns().length}
                        className="text-center py-8 text-muted-foreground"
                      >
                        Nenhuma fatura encontrada. Use os filtros acima para pesquisar.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row: Row<any>) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell: Cell<any, any>) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </div>
        {!error && totalRows > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-background text-sm text-muted-foreground">
            <div>
              Mostrando {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalRows)} de {totalRows}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-muted"
                onClick={() => setPage(0)}
                disabled={currentPage === 0}
              >
                «
              </button>
              <button
                className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-muted"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                ‹
              </button>
              <span className="px-2">
                Página {currentPage + 1} / {totalPages}
              </span>
              <button
                className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-muted"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                ›
              </button>
              <button
                className="px-2 py-1 border rounded disabled:opacity-50 hover:bg-muted"
                onClick={() => setPage(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
              >
                »
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
